import { isCancel } from 'axios'
import { isEmpty, isObject } from 'lodash'
import { api, preferences } from '@/services'

/** @import { Ref } from 'vue' */

export function useWidgetFilters() {
    const widgetFilterHelpers = useWidgetFilterHelpers()
    const { snackbar } = useSnackbars()

    const widgetsStore = useWidgetsStore()

    /** @type {Ref<string | null>} */
    const filterUrl = ref(null)
    const filterRequestController = ref(null)
    const fetchedFiltersByRelativeFilterUrl = ref({})
    const selectedFilters = ref({})

    const readOnlySelectedFilters = computed(() => selectedFilters.value)

    const storageKeyForUrl = (filterUrl) => {
        if (!filterUrl) {
            return ''
        }
        const url = new URL(filterUrl, api.defaults.baseURL)
        // Remove host and query parameters (e.g. signature)
        return url.pathname
    }

    const filterStorageKey = computed(() => {
        return storageKeyForUrl(filterUrl.value)
    })

    const filterGroups = computed(() => {
        if (!filterUrl.value) {
            return []
        }
        return fetchedFiltersByRelativeFilterUrl.value[filterStorageKey.value] ?? []
    })

    const filterCount = computed(() => {
        return widgetFilterHelpers.activeCount(filterGroups.value, selectedFilters.value)
    })

    const preferenceKeyForFilters = computed(() => {
        return `widget-filters:${filterStorageKey.value}:filters`
    })

    const applySelectedFiltersFromPreferences = async () => {
        const rawPreferencesValue = await preferences.getItem(preferenceKeyForFilters.value)

        try {
            const filtersFromPreferences = JSON.parse(rawPreferencesValue ?? '{}')
            if (!isObject(filtersFromPreferences)) {
                throw new Error('Filters from preferences is not an object')
            }
            selectedFilters.value = filtersFromPreferences
        } catch (e) {
            // If we can't parse the filters, clear them to prevent future issues and just return
            await preferences.removeItem(preferenceKeyForFilters.value)
            selectedFilters.value = {}
        }
    }

    const setFilterUrlWithoutFetching = async (url) => {
        filterUrl.value = url
        await applySelectedFiltersFromPreferences()
    }

    /**
     * @param {string} url
     * @param {Boolean} refresh
     * @returns {Promise<void>}
     */
    const fetchFilters = async (url, refresh = false) => {
        filterUrl.value = url

        // Cancel any pending filter request since it's no longer relevant
        if (filterRequestController.value) {
            filterRequestController.value.abort()
        }

        if (!url) {
            return
        }

        const storageKey = filterStorageKey.value
        if (!refresh && fetchedFiltersByRelativeFilterUrl.value[storageKey]) {
            return
        }

        filterRequestController.value = new AbortController()

        return widgetsStore.fetchWidgetShow(url, refresh, { signal: filterRequestController.value.signal })
            .then(async (response) => {
                // Store the filters by widget page URL as the filter URL changes each request due to the signature
                fetchedFiltersByRelativeFilterUrl.value[storageKey] = response.data

                await applySelectedFiltersFromPreferences()
            })
            .catch((e) => {
                if (isCancel(e)) {
                    return
                }
                snackbar({
                    message: 'Error fetching filters',
                    type: 'error',
                })
            })
    }

    const updateSelectedFilters = async (filters) => {
        selectedFilters.value = filters

        // Save preference
        if (isEmpty(filters)) {
            await preferences.removeItem(preferenceKeyForFilters.value)
            return
        }

        await preferences.setItem(preferenceKeyForFilters.value, JSON.stringify(filters))
    }

    return {
        selectedFilters: readOnlySelectedFilters,
        filterGroups,
        filterCount,
        fetchFilters,
        updateSelectedFilters,
        setFilterUrlWithoutFetching,
    }
}
